/**
 * Local-dev CORS proxy routing (feature 002-vite-dev-cors-proxy).
 *
 * Single source of truth shared by two sides that must agree on how backend requests are
 * routed when the local dev proxy is enabled (config.devProxy / VITE_DEV_PROXY=true):
 *  - the API client, via resolveApiBaseUrl() in config/index.ts, which returns the relative
 *    prefix `${DEV_PROXY_PATH_PREFIX}/<key>` instead of the absolute backend origin; and
 *  - the vite dev server, via buildDevProxyMap() consumed by `server.proxy` in vite.config.ts,
 *    which forwards those same-origin paths to the real backend origins.
 *
 * This module (and buildDevProxyMap in particular) is a local-development concern only; the
 * relaxed TLS (`secure: false`) and the proxy itself exist solely for `vite dev` and never enter
 * the built/deployed artifact (spec FR-002, FR-009).
 */

/**
 * Reserved same-origin path prefix for proxied backend requests.
 * Intentionally not a real application route, so it cannot collide with React Router paths,
 * `/healthz`, or `/readiness`.
 */
export const DEV_PROXY_PATH_PREFIX = '/__api';

/**
 * A single vite `server.proxy` entry. Structurally compatible with vite's ProxyOptions; kept as a
 * local interface so this file has no build-time dependency on vite (it is also part of the app bundle).
 */
export interface DevProxyEntry {
  /** Absolute backend origin to forward to. */
  target: string;
  /** Rewrite the forwarded Host header to match the target origin. */
  changeOrigin: boolean;
  /** Relax TLS verification so https backends with unverifiable certs still work (dev server only). */
  secure: boolean;
  /** Strip the `${DEV_PROXY_PATH_PREFIX}/<key>` prefix before forwarding to the backend. */
  rewrite: (path: string) => string;
}

/** Escape a string for safe use inside a RegExp. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Build a vite `server.proxy` map from the named backend map.
 *
 * For every `key → origin` in `apiBaseUrls`, emits a route at `${DEV_PROXY_PATH_PREFIX}/<key>` that
 * forwards to `origin` with `changeOrigin: true`, `secure: false`, and a prefix-stripping rewrite.
 * A single global toggle covers all named backends (spec FR-004).
 */
export function buildDevProxyMap(
  apiBaseUrls: Record<string, string>
): Record<string, DevProxyEntry> {
  const proxy: Record<string, DevProxyEntry> = {};
  for (const [key, origin] of Object.entries(apiBaseUrls)) {
    const route = `${DEV_PROXY_PATH_PREFIX}/${key}`;
    const prefixPattern = new RegExp(`^${escapeRegExp(route)}`);
    proxy[route] = {
      target: origin,
      changeOrigin: true,
      secure: false,
      rewrite: (path: string) => path.replace(prefixPattern, ''),
    };
  }
  return proxy;
}
